import { useEffect, useState, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import useUsuarios from '../../hooks/useUsuarios';
import usePermisos from '../../hooks/usePermisos';
import useAuth from '../../hooks/useAuth';
import DataTable from '../../components/Panel/DataTable';
import BotonAccionIcono from '../../components/Panel/BotonAccionIcono';
import usePaginacionLocal from '../../hooks/usePaginacionLocal';
import useFiltroBusqueda from '../../hooks/useFiltroBusqueda';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

const USUARIO_VACIO = {
  usu_chDoc: '', usu_chNombres: '', usu_chApePat: '', usu_chApeMat: '',
  usu_chDir: '', usu_chTel: '', usu_chEmail: '', password: '', usu_chRol: 'CIUDADANO',
};

/**
 * Gestión de usuarios del panel (HU-003 Fase 4) — listar/filtrar, crear,
 * editar datos, cambiar rol, activar/desactivar, eliminar, e importar por
 * Excel. Exclusiva de personal administrativo (`ADMINISTRADOR`/`SUPERADMIN`,
 * gateado por `RutaProtegidaPanel` en `PanelRoutes`, no acá).
 *
 * `SUPERADMIN` (soporte del proveedor, HU-003 Fase 5) solo aparece como
 * opción de rol para quien YA es `SUPERADMIN` — un `ADMINISTRADOR` del
 * cliente no debe poder auto-promoverse ni ver esa opción en el dropdown
 * (el backend igual lo rechazaría, esto es solo para no mostrar una opción
 * que sabemos que va a fallar).
 */
const GestionUsuarios = () => {
  const { user } = useAuth();
  const {
    loading, error, listarUsuarios, crearUsuario, actualizarUsuario,
    cambiarRol, eliminarUsuario, importarExcel, descargarTemplate,
  } = useUsuarios();
  const { listarRoles } = usePermisos();

  const [nombresRoles, setNombresRoles] = useState([]);
  // `SUPERADMIN` (soporte del proveedor) solo se ofrece como opción a quien
  // YA es `SUPERADMIN` — igual que antes de que los roles fueran dinámicos
  // (HU-003 Fase 5), el backend igual lo rechazaría para cualquier otro.
  const ROLES = user?.usu_chRol === 'SUPERADMIN'
    ? nombresRoles
    : nombresRoles.filter((r) => r !== 'SUPERADMIN');

  const [usuarios, setUsuarios] = useState([]);
  const { busqueda, setBusqueda, itemsFiltrados } = useFiltroBusqueda(
    usuarios,
    (u) => `${u.usu_chDoc} ${u.usu_chNombres} ${u.usu_chApePat} ${u.usu_chApeMat} ${u.usu_chEmail}`
  );
  const { datosPagina, paginacion } = usePaginacionLocal(itemsFiltrados);
  const [filtroRol, setFiltroRol] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('');
  const [formulario, setFormulario] = useState(null); // null | USUARIO_VACIO | usuario existente
  const [mensaje, setMensaje] = useState('');

  const cargar = useCallback(async () => {
    const resultado = await listarUsuarios({ rol: filtroRol, activo: filtroActivo });
    if (resultado.success) setUsuarios(resultado.usuarios);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroRol, filtroActivo]);

  useEffect(() => { cargar(); }, [cargar]);

  // Roles disponibles para los selects de esta pantalla (filtro + alta +
  // cambio de rol inline) — vienen de `Rol` (HU-009-A Fase 3), ya no de una
  // lista fija en código: un rol personalizado creado en "Roles y permisos"
  // aparece acá sin tocar este archivo.
  useEffect(() => {
    listarRoles().then((resultado) => {
      if (resultado.success) setNombresRoles(resultado.roles.map((r) => r.rol_chNombre));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const esEdicion = formulario && formulario.usu_inId;

  const guardarFormulario = async (evento) => {
    evento.preventDefault();
    setMensaje('');
    const resultado = esEdicion
      ? await actualizarUsuario(formulario.usu_inId, formulario)
      : await crearUsuario(formulario);

    if (resultado.success) {
      setFormulario(null);
      cargar();
    } else {
      setMensaje(resultado.error);
    }
  };

  const handleCambiarRol = async (usuario, nuevoRol) => {
    if (nuevoRol === usuario.usu_chRol) return;
    const resultado = await cambiarRol(usuario.usu_inId, nuevoRol);
    if (resultado.success) cargar();
    else setMensaje(resultado.error);
  };

  const handleToggleActivo = async (usuario) => {
    // Misma confirmación que "cerrar sesión" (`useAuth.js`) antes de aplicar
    // el cambio — desactivar a alguien no es una acción para hacer sin
    // querer con un solo clic.
    const activar = !usuario.usu_boActivo;
    const confirmacion = await Swal.fire({
      icon: 'question',
      title: activar ? '¿Activar usuario?' : '¿Desactivar usuario?',
      text: `${usuario.usu_chNombres} ${usuario.usu_chApePat} va a quedar ${activar ? 'activo' : 'inactivo'} en el sistema.`,
      showCancelButton: true,
      confirmButtonText: activar ? 'Sí, activar' : 'Sí, desactivar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1B5FA8',
    });
    if (!confirmacion.isConfirmed) return;

    const resultado = await actualizarUsuario(usuario.usu_inId, { usu_boActivo: activar });
    if (resultado.success) cargar();
    else setMensaje(resultado.error);
  };

  const handleEliminar = async (usuario) => {
    const confirmacion = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar usuario?',
      text: `${usuario.usu_chNombres} ${usuario.usu_chApePat} — esta acción no se puede deshacer.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#e63946',
    });
    if (!confirmacion.isConfirmed) return;
    const resultado = await eliminarUsuario(usuario.usu_inId);
    if (resultado.success) cargar();
    else setMensaje(resultado.error);
  };

  const handleImportar = async (evento) => {
    const archivo = evento.target.files[0];
    if (!archivo) return;
    const resultado = await importarExcel(archivo);
    setMensaje(
      resultado.success
        ? `Importación completa: ${resultado.resultado.usuarios_creados} creados, ${resultado.resultado.usuarios_duplicados} duplicados, ${resultado.resultado.errores} errores.`
        : resultado.error
    );
    evento.target.value = '';
    if (resultado.success) cargar();
  };

  // Una cuenta SUPERADMIN (soporte del proveedor) solo la puede tocar otra
  // cuenta SUPERADMIN — el backend ya lo rechaza (403), acá solo se evita
  // mostrar controles editables que sabemos que van a fallar (HU-003 Fase 5).
  const esCuentaProtegida = (u) => u.usu_chRol === 'SUPERADMIN' && user?.usu_chRol !== 'SUPERADMIN';

  const columnas = useMemo(() => [
    { Header: 'Documento', accessor: 'usu_chDoc' },
    {
      Header: 'Nombre',
      id: 'nombre',
      accessor: (u) => `${u.usu_chNombres} ${u.usu_chApePat}`,
    },
    { Header: 'Email', accessor: 'usu_chEmail' },
    {
      Header: 'Rol',
      accessor: 'usu_chRol',
      Cell: ({ row, value }) => (
        esCuentaProtegida(row.original)
          ? <span className="panel-gu-rol-protegido">{value}</span>
          : (
            <select value={value} onChange={(e) => handleCambiarRol(row.original, e.target.value)} className="panel-input panel-gu-select-rol">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )
      ),
    },
    {
      Header: 'Estado',
      accessor: 'usu_boActivo',
      Cell: ({ row, value }) => (
        <button
          type="button"
          className={`panel-estado-pildora ${value ? 'panel-estado-pildora-activo' : 'panel-estado-pildora-inactivo'}`}
          onClick={() => handleToggleActivo(row.original)}
          disabled={esCuentaProtegida(row.original)}
        >
          <i className={`fas ${value ? 'fa-circle-check' : 'fa-circle-xmark'}`}></i>
          {value ? 'Activo' : 'Inactivo'}
        </button>
      ),
    },
    {
      Header: 'Acciones',
      id: 'acciones',
      Cell: ({ row }) => (
        esCuentaProtegida(row.original) ? (
          <span className="panel-texto-ayuda">Cuenta de soporte</span>
        ) : (
          <div className="panel-gu-fila-acciones">
            <BotonAccionIcono icono="fa-pen" variante="editar" titulo="Editar" onClick={() => setFormulario({ ...row.original })} />
            <BotonAccionIcono icono="fa-trash" variante="eliminar" titulo="Eliminar" onClick={() => handleEliminar(row.original)} />
          </div>
        )
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ROLES, user]);

  return (
    <PanelShell>
      <div className="panel-gu-cabecera">
        <h2>Gestión de usuarios</h2>
        <div className="panel-gu-acciones">
          <button type="button" className="panel-boton-secundario" onClick={descargarTemplate}>
            Descargar plantilla
          </button>
          <label className="panel-boton-secundario panel-gu-label-archivo">
            Importar Excel
            <input type="file" accept=".xlsx,.xls" onChange={handleImportar} hidden />
          </label>
          <button type="button" className="panel-boton-header-accion" onClick={() => setFormulario({ ...USUARIO_VACIO })}>
            <i className="fas fa-plus"></i> Nuevo usuario
          </button>
        </div>
      </div>

      <div className="panel-gu-filtros">
        <input
          className="panel-input"
          placeholder="Buscar por nombre, documento o email…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <select value={filtroRol} onChange={(e) => setFiltroRol(e.target.value)} className="panel-input">
          <option value="">Todos los roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={filtroActivo} onChange={(e) => setFiltroActivo(e.target.value)} className="panel-input">
          <option value="">Activos e inactivos</option>
          <option value="true">Solo activos</option>
          <option value="false">Solo inactivos</option>
        </select>
      </div>

      {mensaje && <p className="panel-gu-mensaje">{mensaje}</p>}
      {error && <p className="panel-gu-mensaje">{error}</p>}

      <DataTable
        columns={columnas}
        data={datosPagina}
        filaVacia={loading ? 'Cargando…' : 'No hay usuarios con estos filtros.'}
        paginacion={paginacion}
      />

      {formulario && (
        <div className="panel-gu-modal-fondo" onClick={() => setFormulario(null)}>
          {/* HU-013: grid 2 columnas en vez de 8 campos apilados uno debajo
              del otro (se veía "como una landing") — la contraseña sigue
              siendo exclusiva del alta (nunca se edita acá, ver
              "Cambiar mi contraseña" en el header, separado a propósito). */}
          <form className="panel-gu-modal panel-usuario-modal" onClick={(e) => e.stopPropagation()} onSubmit={guardarFormulario}>
            <h3>{esEdicion ? 'Editar usuario' : 'Nuevo usuario'}</h3>

            <div className="panel-form-grid-2col">
              <div>
                <label className="panel-label">Documento</label>
                <input className="panel-input" required disabled={!!esEdicion} value={formulario.usu_chDoc}
                  onChange={(e) => setFormulario({ ...formulario, usu_chDoc: e.target.value })} />
              </div>
              <div>
                <label className="panel-label">Nombres</label>
                <input className="panel-input" required value={formulario.usu_chNombres}
                  onChange={(e) => setFormulario({ ...formulario, usu_chNombres: e.target.value })} />
              </div>

              <div>
                <label className="panel-label">Apellido paterno</label>
                <input className="panel-input" required value={formulario.usu_chApePat}
                  onChange={(e) => setFormulario({ ...formulario, usu_chApePat: e.target.value })} />
              </div>
              <div>
                <label className="panel-label">Apellido materno</label>
                <input className="panel-input" required value={formulario.usu_chApeMat}
                  onChange={(e) => setFormulario({ ...formulario, usu_chApeMat: e.target.value })} />
              </div>

              <div>
                <label className="panel-label">Dirección</label>
                <input className="panel-input" required value={formulario.usu_chDir || ''}
                  onChange={(e) => setFormulario({ ...formulario, usu_chDir: e.target.value })} />
              </div>
              <div>
                <label className="panel-label">Teléfono</label>
                <input className="panel-input" required value={formulario.usu_chTel || ''}
                  onChange={(e) => setFormulario({ ...formulario, usu_chTel: e.target.value })} />
              </div>

              <div className={esEdicion ? 'panel-form-grid-2col-ancho' : ''}>
                <label className="panel-label">Email</label>
                <input className="panel-input" type="email" required value={formulario.usu_chEmail}
                  onChange={(e) => setFormulario({ ...formulario, usu_chEmail: e.target.value })} />
              </div>

              {!esEdicion && (
                <>
                  <div>
                    <label className="panel-label">Contraseña</label>
                    <input className="panel-input" type="password" required value={formulario.password}
                      onChange={(e) => setFormulario({ ...formulario, password: e.target.value })} />
                  </div>
                  <div>
                    <label className="panel-label">Rol</label>
                    <select className="panel-input" value={formulario.usu_chRol}
                      onChange={(e) => setFormulario({ ...formulario, usu_chRol: e.target.value })}>
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="panel-gu-modal-botones">
              <button type="button" className="panel-boton-secundario" onClick={() => setFormulario(null)}>Cancelar</button>
              <button type="submit" className="panel-boton-primario" disabled={loading}>
                {loading ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </PanelShell>
  );
};

export default GestionUsuarios;
